/**
 * La présence du chatteur — pour que « qui est là » soit VRAI.
 *
 * Brique 1 de la distribution des discussions (Kayla, 09-09-2026). Tant
 * qu'une session de shift est en cours (`shiftSessions`, clock-in fait,
 * clock-out pas encore), le kiosque bat vers `POST /api/presence` toutes les
 * 30 s — onglet visible seulement (cf `presenceLogic.ts`). Le serveur en tire
 * `GET /api/presence` : qui est présent, en pause ou pas, par créatrice.
 *
 *   - `paused` suit le geste « Pause » du shift (`breakStartedAt`), lu au même
 *     endroit que le bandeau de shift ; un changement de pause bat tout de
 *     suite, il n'attend pas le tick ;
 *   - au clock-out, la session cesse d'être `active` : on s'arrête, et on le
 *     dit une dernière fois (`paused`) pour ne pas laisser un fantôme ;
 *   - à la fermeture de l'onglet (`pagehide`), même chose par `sendBeacon` :
 *     c'est la seule requête qu'un navigateur promet d'envoyer en partant. En
 *     prod l'API est servie sur la même origine que le cockpit, le cookie de
 *     session part avec.
 *
 * ⛔ Rien ici ne vient de la Régie (`/api/regie/*`, `RegiePanel`) : c'est du
 * legacy, Kayla l'a dit le 09-09. Ce hook vit dans le shell (`CockpitShell`),
 * pas dans un panneau — la présence ne dépend pas de l'onglet ouvert.
 *
 * 🩸 Monté pour TOUT LE MONDE, et c'est le shift gate seul qui décide. La
 * première version ne le montait que pour le kiosque « chatter »
 * (`KIOSK_SHIFT_GATED_ROLES`) — et n'a jamais battu : mesuré le 09-09, la
 * chatteuse pointée sur le Shift C porte le rôle `operator`, que
 * `kioskRoleFor` ne reconnaît pas comme chatteur (il a `creators.view`), alors
 * que le gate lui impose bien une session. Le rôle n'est pas la bonne
 * question ; « a-t-il une session en cours ? » l'est, et le gate y répond
 * `active` pour lui seul — `bypass` pour Kayla et les managers, qui n'ont
 * pas de session et ne battent pas.
 */
import { useEffect, useRef } from "react";

import { api, buildUrl } from "@/core/api/client";
import { useActiveAccountId } from "@/core/auth/activeAccount";
import { useModeDemo } from "@/core/demo";
import { useShiftGate } from "@/panels/conversations/useShiftGate";

import { PRESENCE_BATTEMENT_MS, battreLaPresence, type Battement } from "./presenceLogic";

const CHEMIN = "/api/presence";

/**
 * Le dernier mot avant de partir : `paused`. Par `sendBeacon` quand le
 * navigateur l'offre (il survit à la fermeture de l'onglet), sinon un POST
 * ordinaire dont on n'attend rien.
 */
export function signalerLeDepart(): void {
  const corps = JSON.stringify({ paused: true });
  if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    navigator.sendBeacon(buildUrl(CHEMIN), new Blob([corps], { type: "application/json" }));
    return;
  }
  void api.post(CHEMIN, { paused: true }).catch(() => {
    // On part : personne pour lire une erreur, et le serveur expire de toute
    // façon le dernier battement en 90 s.
  });
}

export function useChatterPresence(): void {
  // 🎬 En démo, le shift est un faux (atom local) : on ne signale rien.
  const enDemo = useModeDemo();
  const accountId = useActiveAccountId();
  const gate = useShiftGate(enDemo ? null : accountId);

  // 🩸 La SESSION DU SERVEUR, pas `state.kind === "active"`. Ce dernier ajoute
  // une condition d'horloge (« l'heure de ce poste, corrigée, est avant la fin
  // prévue ») — et le 09-09-2026 c'est elle qui a éteint le battement d'une
  // chatteuse pointée, une heure avant la fin réelle de sa session. Le serveur
  // ouvre et ferme les sessions ; la présence l'écoute lui, et lui seul.
  const sessionEnCours = !enDemo && gate.sessionOuverte;
  const enPause = sessionEnCours && gate.enPauseDeShift;

  // Le battement lit la DERNIÈRE valeur sans se réarmer : réarmer l'effet à
  // chaque pause/reprise casserait la cadence.
  const enPauseRef = useRef(enPause);
  enPauseRef.current = enPause;
  const battementRef = useRef<Battement | null>(null);

  useEffect(() => {
    if (!sessionEnCours) return;
    const battement = battreLaPresence(
      () => api.post(CHEMIN, { paused: enPauseRef.current }),
      PRESENCE_BATTEMENT_MS,
    );
    battementRef.current = battement;
    window.addEventListener("pagehide", signalerLeDepart);
    return () => {
      battement.arreter();
      battementRef.current = null;
      window.removeEventListener("pagehide", signalerLeDepart);
      // Clock-out, fin de fenêtre, ou on quitte le kiosque : on le dit.
      signalerLeDepart();
    };
  }, [sessionEnCours]);

  // Pause posée ou levée → le serveur le sait tout de suite.
  const pausePrecedente = useRef(enPause);
  useEffect(() => {
    if (pausePrecedente.current === enPause) return;
    pausePrecedente.current = enPause;
    battementRef.current?.maintenant();
  }, [enPause]);
}
